import { useCallback, useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query. Used to pick between the three deliberate
 * layout shapes (spec §8.2–§8.4) instead of letting the desktop layout shrink.
 *
 * @param {string} query
 * @returns {boolean}
 */
export function useMediaQuery(query) {
  const read = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState(read);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    setMatches(list.matches);
    // Safari < 14 only has the deprecated add/removeListener pair.
    if (list.addEventListener) {
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    }
    list.addListener(onChange);
    return () => list.removeListener(onChange);
  }, [query]);

  return matches;
}

/**
 * Live online/offline flag so every panel can render the mandated offline
 * state (spec §11) rather than a generic error.
 */
export function useOnlineStatus() {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}

/**
 * JSON-backed localStorage state that never throws in private-mode browsers.
 *
 * @template T
 * @param {string} key
 * @param {T} initial
 * @returns {[T, (next: T | ((prev: T) => T)) => void]}
 */
export function usePersistentState(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return initial;
      const parsed = JSON.parse(stored);
      return parsed && typeof parsed === 'object' && typeof initial === 'object' && initial !== null
        ? { ...initial, ...parsed }
        : parsed;
    } catch {
      return initial;
    }
  });

  const update = useCallback(
    (next) => {
      setValue((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        try {
          localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // Quota or private mode — the UI still works, it just forgets.
        }
        return resolved;
      });
    },
    [key]
  );

  return [value, update];
}
